/* eslint-disable no-restricted-globals */
import React from 'react';
import styled from 'styled-components';
import { Container } from 'react-bootstrap';
import {
  BREAKPOINTS,
  PRIMARY_COLOR,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from '../../core/constants';
import { LocalesUnion } from '../../types/interfaces';
import useTranslation from '../../hooks/useTranslation';

const Wrapper = styled.section`
  background: linear-gradient(184deg, rgb(252, 253, 253), rgb(248, 249, 250));
  padding-top: 50px;
  min-height: 600px;
  border-bottom: 5px solid #eee;

  @media ${BREAKPOINTS.lg} {
    margin-bottom: 60px;
  }
`;

const Content = styled.div`
  z-index: 10;
  object-fit: cover;
  display: flex;
  flex-direction: row;
  -webkit-box-pack: center;
  justify-content: flex-end;
  position: relative;
  width: 100%;
`;

const LeftSection = styled.section`
  box-sizing: border-box;
  flex-flow: column wrap;
  display: flex;
  align-items: flex-start;
  flex: 1;
  z-index: 1;
  padding-right: 20px;

  @media ${BREAKPOINTS.lg} {
    padding-right: 0px;
  }
`;

const RightSection = styled.section`
  flex-wrap: wrap;
  flex: 1;
  max-height: 600px;
  overflow: hidden;
`;

const PhoneMask = styled.img`
  object-fit: cover;
  height: 100%;
  width: 100%;
  z-index: 10;
  transition: all 400ms ease-in 0s;
`;

const PhoneShadow = styled.div`
  box-shadow: rgb(0 0 0 / 10%) 4px 16px 34px 0px;
  position: absolute;
  border-radius: 67px;
  padding-top: 12px;
  padding-bottom: 12px;
  box-sizing: border-box;
  width: 95%;
  height: 98%;
  top: 0px;
  bottom: 0px;
`;

const HeroScreenshot = styled.img`
  position: absolute;
  height: 100%;
  padding-top: 20px;
  padding-bottom: 12px;
  top: -2px;
  z-index: -1;
  object-fit: contain;
  width: 95%;
  align-self: center;
  height: 100%;
  left: 3%;
`;

const LeftTooltip = styled.img`
  position: absolute;
  z-index: 15;
  width: 460px;
  left: 0px;
  top: 300px;
`;

const Title = styled.h2`
  font-weight: bold;
  font-style: normal;
  font-size: 40px;
  line-height: 50px;
  margin-bottom: 15px;
  margin-top: 20px;
  z-index: 3;
  color: ${TEXT_PRIMARY};

  @media ${BREAKPOINTS.lg} {
    text-align: center;
    font-size: 36px;
    align-self: center;
  }
`;

const SubTitle = styled.p`
  -webkit-font-smoothing: antialiased;
  z-index: 3;
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 30px;
  font-weight: 400;
  color: ${TEXT_SECONDARY};

  @media ${BREAKPOINTS.lg} {
    text-align: center;
    font-size: 16px;
  }
`;

const Button = styled.button`
  border-radius: 3px;
  flex-direction: row;
  font-size: 14px;
  letter-spacing: 1px;
  padding: 12px 20px;
  transition: transform 0.2s ease 0s;
  color: ${PRIMARY_COLOR};
  border: 0px;
  box-shadow: ${PRIMARY_COLOR} 0px 0px 0px 1px inset;
  background: transparent;
  display: flex;

  @media ${BREAKPOINTS.lg} {
    align-self: center;
  }
`;

const Separator = styled.svg`
  margin: 60px 0px 15px;
`;

const Quote = styled.p`
  -webkit-font-smoothing: antialiased;
  font-size: 16px;
  line-height: 26px;
  color: ${TEXT_SECONDARY};
  margin-top: 5px;
  margin-bottom: 5px;
`;

const QuoteAuthor = styled.span`
  margin-top: 20px;
`;

const Box = styled(Container)`
  display: flex;
  min-height: 600px;

  @media ${BREAKPOINTS.lg} {
    flex-direction: column;
  }
`;

const ScreenShotContainer = styled.div`
  width: 80%;
  position: relative;
  display: flex;

  @media ${BREAKPOINTS.xl} {
    width: 70%;
  }

  @media ${BREAKPOINTS.lg} {
    width: 70%;
    margin-top: 30px;
  }

  @media ${BREAKPOINTS.sm} {
    width: 100%;
    margin-top: 30px;
  }
`;

interface CProps {
  locale: LocalesUnion;
}

const HeroPhone: React.FC<CProps> = ({ locale }) => {
  const { t } = useTranslation(locale);

  return (
    <Wrapper>
      <Box>
        <LeftSection>
          <Title>{t('HOMEPAGE_HERO_PHONE_TITLE')}</Title>
          <SubTitle>{t('HOMEPAGE_HERO_PHONE_DESC')}</SubTitle>
          <Button
            type="button"
            onClick={() => window.open('http://onelink.to/deevent')}
          >
            {t('HOMEPAGE_HERO_PHONE_BUTTON')}
          </Button>
          <Separator viewBox="0 0 327 3" version="1.1">
            <g
              id="Landing-Page"
              stroke="none"
              strokeWidth="1"
              fill="none"
              fillRule="evenodd"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <g
                id="1.1-Mobile-Menu"
                transform="translate(-24.000000, -314.000000)"
                stroke="#333"
                strokeWidth="1.2"
              >
                <path
                  d="M25,315.327062 C68.6493674,315.844158 116.913514,315.844158 169.792439,315.327062 C249.110827,314.551417 279.405808,315.310283 350,316"
                  id="Path-8-Copy-3"
                />
              </g>
            </g>
          </Separator>
          <Quote>
            {t('HOMEPAGE_HERO_PHONE_QUOTE')}
            <br />
            <QuoteAuthor>{t('HOMEPAGE_HERO_PHONE_QUOTE_AUTHOR')}</QuoteAuthor>
          </Quote>
        </LeftSection>
        <RightSection>
          <Content>
            <LeftTooltip src="/images/tooltip-tour.png" />
            <ScreenShotContainer>
              <PhoneMask
                width="400"
                height="300"
                src="/images/phone-mask.png"
              />
              <PhoneShadow />
              <HeroScreenshot
                width="400"
                height="300"
                src="/images/hero-phone-2.png"
              />
            </ScreenShotContainer>
          </Content>
        </RightSection>
      </Box>
    </Wrapper>
  );
};

export default HeroPhone;
